import { useCallback, useEffect, useState } from 'react';

import classNames from 'classnames';
import find from 'lodash/find';
import isEmpty from 'lodash/isEmpty';
import partialRight from 'lodash/partialRight';

import type { UnifiedEvent } from 'src/app/analytics/analyticsEvents';
import { UNIFIED, ANALYTICS_EVENTS } from 'src/app/analytics/analyticsEvents';
import SubmitButton from 'src/app/components/Elements/Button/Submit';
import { Card } from 'src/app/components/Elements/Card';
import LinkToCardItem from 'src/app/components/Elements/Card/LinkToCardItem';
import RadioCardItem from 'src/app/components/Elements/Card/RadioCardItem';
import StandardPage from 'src/app/components/Elements/Page/Standard';
import FixedFloat from 'src/app/components/Elements/Page/Standard/FixedFloat';
import { Typography } from 'src/app/components/Elements/Typography';
import { TypographyRows } from 'src/app/components/Elements/Typography/Sections';
import DeliveryAddress from 'src/app/components/Models/DeliveryAddress/container';
import IdVerificationRequired from 'src/app/components/Pages/Checkout/IdVerificationRequired/container';
import { CardItemOverflowMenu } from 'src/app/components/PageSections/CardItemOverflowMenuWrapper';
import { useSegmentTrack } from 'src/app/hooks/useSegmentTrack';
import { checkoutAddAddress } from 'src/app/router/paths';
import {
  clearAddressValidationAction,
  clearEditAddressState,
  closeModal,
  editDeliveryAddress,
  openModal,
  segmentAnalyticsEvent,
  segmentTrack,
} from 'src/app/state/actions';
import { accountLoadEverything, deleteAccountAddress, loadDeliveryAddress } from 'src/app/state/actions/account';
import { bffFetchCart, loadCartDeliveryMethods, updateCartDeliveryAddress } from 'src/app/state/actions/cart';
import { pushNextCheckoutState } from 'src/app/state/actions/checkout';
import * as actionTypes from 'src/app/state/actions/types';
import {
  getBFFCart,
  getBFFCartDeliveryAddress,
  getCurrentAccountId,
  getCurrentCartId,
  getCurrentCartUUID,
  getDeliveryAddresses,
  isDeliveryMethodsESIMailEligible,
} from 'src/app/state/selectors';
import { useSelector, useDispatch } from 'src/app/state/store';
import type { UpdateCartDeliveryAddressRequest } from 'src/app/types';
import { CAPSULE_ICON_STRINGS, ESI_MAIL_IDENTIFY_KEY, POPUP_MENU_LABELS } from 'src/consts';

import { AddANewAddressContainer, AddIcon } from './styles';
import styles from './styles.module.scss';

export const DeliveryAddressSelect = () => {
  const cartId = useSelector(getCurrentCartId);
  const cartUUID = useSelector(getCurrentCartUUID);

  const accountId = useSelector(getCurrentAccountId);
  const bffCart = useSelector(getBFFCart);
  const bffCartDeliveryAddress = useSelector(getBFFCartDeliveryAddress);
  const deliveryAddresses = useSelector(getDeliveryAddresses);
  const defaultAddress = find(deliveryAddresses, deliveryAddress => deliveryAddress.isDefault);

  const selectedDeliveryAddressId = bffCartDeliveryAddress?.id || defaultAddress?.id;

  const [selectedAddress, setSelectedAddress] = useState(
    bffCartDeliveryAddress ? bffCartDeliveryAddress : defaultAddress ? defaultAddress : null
  );

  const dispatch = useDispatch();

  const dispatchLoadCart = cartId => dispatch(bffFetchCart(cartId));
  const dispatchLoadAccount = (withDeliveryAddress: boolean) => dispatch(accountLoadEverything(withDeliveryAddress));
  const dispatchLoadDeliveryAddresses = () => dispatch(loadDeliveryAddress());
  const dispatchGotoAddAddress = () => {
    dispatch(clearEditAddressState());
    dispatch(clearAddressValidationAction());
  };
  const dispatchAccountDeleteAddress = (addressId, accountId) => dispatch(deleteAccountAddress(addressId, accountId));
  const dispatchOpenModal = ({ modalType, handleConfirmClick, confirmMessage }) =>
    dispatch(openModal({ modalType, handleConfirmClick, confirmMessage }));
  const dispatchCloseModal = () => dispatch(closeModal());
  const dispatchEditAddress = address => {
    return dispatch(editDeliveryAddress(address, cartUUID));
  };
  const dispatchTrackAddressChange = addressChange =>
    dispatch(segmentAnalyticsEvent(`${ANALYTICS_EVENTS.addressChangePrefix}${addressChange}${' Address'}`));
  const dispatchUpdateCartAndPushNextCheckoutState = async (
    deliveryAddressBody: UpdateCartDeliveryAddressRequest,
    accountId: string
  ) => {
    await dispatch(updateCartDeliveryAddress(deliveryAddressBody));
    // Need to load delivery methods before pushing the next checkout state as product
    // wants us to skip the delivery method screen if ESI_MAIL is not an option
    const deliveryMethods = await dispatch(loadCartDeliveryMethods());
    const hasESIMailAsAnOption = isDeliveryMethodsESIMailEligible(deliveryMethods);
    dispatch({
      type: actionTypes.SEGMENT_IDENTIFY,
      accountId,
      traits: { [ESI_MAIL_IDENTIFY_KEY]: hasESIMailAsAnOption ? 1 : 0 },
    });
    await dispatch(pushNextCheckoutState());
  };
  const dispatchSegmentTrack = useCallback(
    (event: UnifiedEvent, payload: any) => dispatch(segmentTrack(event, payload) as any),
    [dispatch]
  );

  useSegmentTrack(UNIFIED.viewCheckoutDeliveryAddress);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleDeleteAddress = async ({ addressId, accountId }) => {
    setIsSubmitting(true);
    dispatchCloseModal();
    await dispatchAccountDeleteAddress(addressId, accountId);
    dispatchSegmentTrack(UNIFIED.deleteDeliveryAddress, { location: 'Checkout' });
    setIsSubmitting(false);
  };

  const handleDelete = (deliveryAddress: { id: any }) => {
    dispatchOpenModal({
      modalType: 'confirm',
      handleConfirmClick: () => handleDeleteAddress({ addressId: deliveryAddress.id, accountId: accountId }),
      confirmMessage: 'Are you sure you want to delete this address?',
    });
  };
  const handleEdit = (deliveryAddress: { id: any }) => {
    dispatchTrackAddressChange(POPUP_MENU_LABELS.edit);
    return dispatchEditAddress(deliveryAddress);
  };

  useEffect(() => {
    if (isEmpty(deliveryAddresses)) {
      dispatchLoadAccount(false);
    }
    dispatchLoadDeliveryAddresses();
    if (!bffCart) {
      dispatchLoadCart(cartId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectDeliveryAddress = (e, deliveryAddress) => {
    e.preventDefault();
    dispatchUpdateCartAndPushNextCheckoutState({ cartUUID, deliveryAddressId: deliveryAddress?.id }, accountId);
    setIsSubmitting(true);
  };

  const radioCardSelected = async (deliveryAddress: any) => {
    if (selectedAddress?.id !== deliveryAddress?.id) {
      await dispatch(updateCartDeliveryAddress({ cartUUID, deliveryAddressId: deliveryAddress?.id }));
    }
    setSelectedAddress(deliveryAddress);
  };

  const getMenuActions = (deliveryAddress: { id: any }) => {
    const menuActions = {
      items: [{ label: POPUP_MENU_LABELS.edit, action: () => handleEdit(deliveryAddress) }],
    };
    if (selectedAddress?.id !== deliveryAddress?.id) {
      menuActions.items.push({ label: POPUP_MENU_LABELS.delete, action: async () => handleDelete(deliveryAddress) });
    }
    return menuActions;
  };

  return (
    <StandardPage alignment={'left'}>
      <div className={classNames(styles.medicationHeaderWrapper)}>
        <Typography variant={'h1'} marginBottom={'s3'}>
          Choose a delivery address
        </Typography>
      </div>
      <div>
        <IdVerificationRequired />

        <Card>
          {deliveryAddresses?.map((deliveryAddress, index) => (
            <RadioCardItem
              showMenuActions={true}
              key={deliveryAddress.id}
              onClick={() => {
                radioCardSelected(deliveryAddress);
              }}
              isActive={
                selectedAddress
                  ? selectedAddress.id === deliveryAddress.id
                  : selectedDeliveryAddressId === deliveryAddress.id
              }
              data_cy={`delivery_address_select_${index}`}
            >
              <div className={styles.menu}>
                <CardItemOverflowMenu menuActions={getMenuActions(deliveryAddress)} />
              </div>
              <DeliveryAddress
                DeliveryAddress={{
                  attributes: {
                    street: deliveryAddress?.address,
                    street_two: deliveryAddress?.addressTwo,
                    city: deliveryAddress?.city,
                    state: deliveryAddress?.state,
                    zip_code: deliveryAddress?.zipCode,
                    is_doorman: deliveryAddress?.isDoorman,
                    notes: deliveryAddress?.instructions,
                  },
                }}
              />
            </RadioCardItem>
          ))}
          <LinkToCardItem
            to={checkoutAddAddress.toPath({ cartId })}
            onClick={() => dispatchGotoAddAddress()}
            data-cy={'add-address'}
            showArrow={false}
          >
            <AddANewAddressContainer>
              <AddIcon iconString={CAPSULE_ICON_STRINGS.add_icon} block />
              <TypographyRows textList={['Add a new address']} />
            </AddANewAddressContainer>
          </LinkToCardItem>
        </Card>
        <div className={styles.space} />
        <FixedFloat>
          <SubmitButton
            style="continueButton"
            isSubmitting={false}
            data-cy={'checkout-place-order'}
            onClick={partialRight(selectDeliveryAddress, selectedAddress)}
            disabled={isSubmitting || isEmpty(deliveryAddresses)}
          >
            Continue
          </SubmitButton>
        </FixedFloat>
      </div>
    </StandardPage>
  );
};
